import CouncillorProfile from "components/CouncillorProfile";
import CouncillorManagement from "components/Modal/CouncillorManagement";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  fetchCounsilors,
  fetchCounsilorsById,
  modifyCounsilors,
} from "api/counsilors";
import { fetchWards } from "api/location";
import DeleteCounsilor from "components/Modal/DeleteCounsilor";
import ButtonComp from "themes/elements/Button";
import CouncillorsTable from "themes/elements/Table/CouncillorsTable";
import { isPermit } from "utils/permissionCheck";
import utils from "utils/utils";
import { getColumnConfig } from "./config";
import {
  setCurrentPageNo,
  setDeleteUserLoading,
  setLoading,
  setProfileView,
  setSelectedCounsilor,
  setTotalPageNo,
  setUserDeleteModalOpen,
  setWards,
} from "./counsilorSlice";

function Councillor() {
  const dispatch = useDispatch();
  const {
    isLoading,
    profileView,
    currentPageNo,
    totalPageNo,
    wards,
    selectedCounsilor,
  } = useSelector((state) => state.counsilor);

  const [addModal, setAddModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [counsilorData, setCounsilorData] = useState([]);

  const [filterName, setFilterName] = useState("");
  const [filterMail, setFilterMail] = useState("");
  const [filterPhone, setFilterPhone] = useState();
  const [filterWard, setFilterWard] = useState();

  const [filterTerm, setFilterTerm] = useState();
  const isFirstLoad = useRef(true);

  const [filterStatus, setFilterStatus] = useState({ value: 2, name: "All" });

  // const [filterType, setFilterType] = useState({ name: "All", value: 3 });

  useEffect(() => {
    dispatch(setCurrentPageNo(1)); // Set the page number to 1
  }, [dispatch]);

  useEffect(() => {
    if (currentPageNo === 1 && isFirstLoad.current) {
      dispatch(setProfileView(false));
      fetchCouncillors(); // Trigger the initial data load after currentPageNo is set
      isFirstLoad.current = false; // Set first load flag to false
    }
  }, [currentPageNo]);

  useEffect(() => {
    if (!isFirstLoad.current) {
      fetchCouncillors();
    }
  }, [
    currentPageNo,
    filterName,
    profileView,
    filterMail,
    filterPhone,
    filterWard,
    filterStatus,
    filterTerm,
  ]);

  useEffect(() => {
    const loadWards = async () => {
      try {
        const wards = await fetchWards();
        const wardOptions = wards.map((ward) => ({
          label: `${ward.ward_code} - ${ward.region_name}`,
          value: `${ward.ward_code} `,
          wardCode: Number(ward.ward_code),
          wardName: ward?.ward_name,
          regionId: Number(ward.region_id),
          regionName: ward.region_name,
          wardStatusFlag: ward.ward_status_flg,
        }));
        if (!utils.isEmpty(wardOptions)) {
          dispatch(setWards([...wardOptions]));
        }
      } catch (error) {}
    };

    loadWards();
  }, []);

  const fetchCouncillors = async () => {
    dispatch(setLoading(true));
    try {
      // Constructing parameters dynamically to avoid sending undefined or empty values
      const params = {
        page: currentPageNo,
        perPage: 10,
        status: filterStatus.value, // Assuming 'limit' and 'perPage' are meant to serve the same purpose, you might consider keeping just one of them if they are redundant.
      };
      const reformatDate = (dateStr) => {
        const parts = dateStr.split("-");
        return `${parts[2]}-${parts[1]}-${parts[0]}`; // Concatenate in the new order
      };

      if (filterName) params.councilorName = filterName;
      if (filterMail) params.email = filterMail;
      if (filterPhone) params.phoneNumber = filterPhone; // Note: Typo here, should be 'phoneNumber' if your API expects that.
      if (filterWard) params.wardCode = filterWard;
      if (filterTerm) {
        params.termStartDate = reformatDate(filterTerm[0]);
        params.termEndDate = reformatDate(filterTerm[1]);
      }

      const response = await fetchCounsilors(params);

      if (response?.councilors) {
        setCounsilorData(response.councilors);
      }
      if (response?.meta) {
        if (currentPageNo === 1) {
          dispatch(setTotalPageNo(response.meta.last_page * 10)); // Changed to totalPages, if your API returns total pages directly, use that instead of last_page * 10
        }
        dispatch(setCurrentPageNo(response.meta.current_page)); // Use currentPage directly if it's provided
      }
    } catch (error) {
    } finally {
      dispatch(setLoading(false));
    }
  };
  const fetchCouncillor = async (councilorId) => {
    try {
      const params = {
        page: 1,
        perPage: 100,
        status: 2,
        councilorId: councilorId
          ? councilorId
          : selectedCounsilor?.councilor_id,
      };
      const response = await fetchCounsilorsById(params);
      dispatch(setSelectedCounsilor(response?.councilors[0]));
    } catch (error) {
    } finally {
      // dispatch(setLoading(false));
    }
  };

  const deleteUserData = async () => {
    dispatch(setDeleteUserLoading(true));
    try {
      const response = await modifyCounsilors({
        action: "delete",
        firstName: selectedCounsilor.first_name,
        lastName: selectedCounsilor.last_name,
        email: selectedCounsilor.email,
        phoneNumber: selectedCounsilor.phone_number,
        councilorId: selectedCounsilor.councilor_id,
      });
      if (response?.message) {
        toast.success(response.message);
        fetchCouncillors();
        // Toast("success", response?.message);
        dispatch(setDeleteUserLoading(false));

        dispatch(setUserDeleteModalOpen(false));
      }
    } catch (error) {
      dispatch(setDeleteUserLoading(false));
    } finally {
      dispatch(setDeleteUserLoading(false));
    }
  };

  return (
    <>
      {profileView ? (
        <CouncillorProfile
          fetchCouncillor={fetchCouncillor}
          isDetails={selectedCounsilor}
          setIsEdit={setIsEdit}
        />
      ) : (
        <div className="page-wrapper flex flex-column gap-2">
          <div className="flex wrap gap-2 align-center justify-between">
            <h2 className="page-title">Councillors</h2>
            {isPermit(7, "create") && (
              <ButtonComp
                onClick={() => {
                  setIsEdit(false);
                  dispatch(setSelectedCounsilor(null));
                  setAddModal(true);
                }}
                prefix
              >
                Add councillor
              </ButtonComp>
            )}
          </div>
          {isPermit(7, "view") && (
            <CouncillorsTable
              filterName={filterName}
              setFilterMail={setFilterMail}
              filterMail={filterMail}
              setFilterPhone={setFilterPhone}
              filterPhone={filterPhone}
              setFilterName={setFilterName}
              setFilterWard={setFilterWard}
              setFilterStatus={setFilterStatus}
              filterStatus={filterStatus}
              filterWard={filterWard}
              setFilterTerm={setFilterTerm}
              filterTerm={filterTerm}
              isLoading={isLoading}
              onDataClick={(data) => {
                dispatch(setSelectedCounsilor(data));
                dispatch(setProfileView(true));
              }}
              onDeleteAction={(data) => {
                dispatch(setSelectedCounsilor(data));
                dispatch(setUserDeleteModalOpen(true));
              }}
              handleEdit={(data) => {
                setIsEdit(true);
                dispatch(setSelectedCounsilor(data));
                setAddModal(true);
              }}
              column={getColumnConfig(wards)}
              copyAction={false}
              totalPageNo={totalPageNo}
              data={counsilorData}
            />
          )}
        </div>
      )}
      <CouncillorManagement
        isEdit={isEdit}
        data={isEdit && selectedCounsilor}
        setOpen={setAddModal}
        open={addModal}
        fetchCouncillor={fetchCouncillor}
        fetchCouncillors={fetchCouncillors}
      />
      <DeleteCounsilor
        selectedData={selectedCounsilor}
        data={selectedCounsilor}
        handleDeleteNotification={deleteUserData}
      />
    </>
  );
}

export default Councillor;
